// Full case-study content for each portfolio project.

const CASE_STUDIES = [
  {
    slug: "vr-safety-training",
    category: "Extended Reality",
    title: "Immersive VR Safety Training",
    summary:
      "A room-scale VR program that lets industrial workers rehearse high-risk procedures in a photoreal digital twin of their own plant — with zero real-world danger.",
    heroImage:
      "https://images.pexels.com/photos/7561900/pexels-photo-7561900.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
    client: "Global Manufacturing Group",
    industry: "Industrial / Manufacturing",
    duration: "16 weeks",
    deliverables: ["VR Application", "Digital Twin Environment", "Instructor Dashboard"],
    metrics: [
      { value: "-62%", label: "Safety incidents in first year" },
      { value: "4x", label: "Faster onboarding to competency" },
      { value: "98%", label: "Learner completion rate" },
    ],
    challenge:
      "Traditional classroom safety training wasn't translating to the shop floor. Workers memorised procedures but froze during real emergencies, and live drills on active machinery were expensive, disruptive and risky.",
    approach: [
      {
        title: "Digital twin of the facility",
        body:
          "We laser-scanned the plant and rebuilt it as a to-scale interactive environment, so every hazard and control matched reality precisely.",
      },
      {
        title: "Scenario-based rehearsal",
        body:
          "Learners practise lockout-tagout, spill response and evacuation as branching scenarios that adapt to their decisions in real time.",
      },
      {
        title: "Measured mastery",
        body:
          "An instructor dashboard tracks hesitation, errors and completion, feeding assessment data straight back into the LMS.",
      },
    ],
    results: [
      "62% reduction in reportable safety incidents within the first year",
      "New hires reached competency 4x faster than with classroom training",
      "Repeatable drills with no downtime on live equipment",
      "Data-driven certification integrated with the client's LMS",
    ],
    services: ["VR Development", "Digital Twins", "3D Simulation", "Instructional Design", "XR Training"],
    gallery: [
      "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.unsplash.com/photo-1576633587382-13ddf37b1fc1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.pexels.com/photos/7561899/pexels-photo-7561899.jpeg?auto=compress&cs=tinysrgb&w=1200",
    ],
  },
  {
    slug: "ar-product-showcase",
    category: "Augmented Reality",
    title: "AR Product Showcase",
    summary:
      "A web-based augmented reality experience that lets customers place life-size products in their own space — driving confidence and conversions with no app download.",
    heroImage:
      "https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    client: "Consumer Electronics Brand",
    industry: "Retail / Consumer Tech",
    duration: "10 weeks",
    deliverables: ["WebAR Experience", "3D Product Models", "Analytics Integration"],
    metrics: [
      { value: "+38%", label: "Add-to-cart conversion" },
      { value: "-27%", label: "Product returns" },
      { value: "0", label: "App downloads required" },
    ],
    challenge:
      "Shoppers loved the product photos but struggled to imagine scale and fit in their own homes — leading to hesitation at checkout and a high rate of returns.",
    approach: [
      {
        title: "Photoreal 3D models",
        body:
          "We built accurate, materials-correct 3D assets optimised to load in seconds directly in the mobile browser.",
      },
      {
        title: "Frictionless WebAR",
        body:
          "One tap places the product in the room at true scale — no app install, working across iOS and Android.",
      },
      {
        title: "Conversion analytics",
        body:
          "We instrumented every interaction so marketing could see which products and angles drove the most intent.",
      },
    ],
    results: [
      "38% lift in add-to-cart conversion for AR-enabled products",
      "27% drop in returns thanks to accurate scale visualisation",
      "Instant experience with zero app friction",
      "Actionable engagement data feeding the marketing team",
    ],
    services: ["AR Development", "3D Modelling", "WebAR", "XR Marketing"],
    gallery: [
      "https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.unsplash.com/photo-1576633587382-13ddf37b1fc1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    ],
  },
  {
    slug: "enterprise-learning-platform",
    category: "Digital Learning",
    title: "Enterprise Learning Platform",
    summary:
      "A homegrown LMS and content ecosystem that unified fragmented training across a global workforce — with gamified microlearning and localization into multiple languages.",
    heroImage:
      "https://images.pexels.com/photos/3807755/pexels-photo-3807755.jpeg?auto=compress&cs=tinysrgb&w=1600",
    client: "Multinational Services Firm",
    industry: "Enterprise / L&D",
    duration: "24 weeks",
    deliverables: ["Homegrown LMS", "120+ Microlearning Modules", "Localization (12 languages)"],
    metrics: [
      { value: "+90%", label: "Course completion" },
      { value: "12", label: "Languages localized" },
      { value: "120+", label: "Modules delivered" },
    ],
    challenge:
      "Training lived in a dozen disconnected tools across regions. Completion rates were low, content was inconsistent, and leadership had no single view of workforce capability.",
    approach: [
      {
        title: "One platform, built to fit",
        body:
          "We deployed our homegrown LMS, customised to the client's roles, workflows and enterprise SSO, replacing the tool sprawl.",
      },
      {
        title: "Gamified microlearning",
        body:
          "Long courses were reimagined as bite-sized, gamified modules with streaks and badges to keep learners coming back.",
      },
      {
        title: "Global from day one",
        body:
          "Every module was localized into 12 languages with professional voice-over and dubbing for regional relevance.",
      },
    ],
    results: [
      "Course completion climbed above 90% across regions",
      "A single source of truth for workforce capability",
      "Consistent, on-brand learning in 12 languages",
      "Administrative overhead cut dramatically with one platform",
    ],
    services: [
      "LMS Platform",
      "LMS Customization",
      "Microlearning",
      "Gamified Learning",
      "Localization",
      "Voice-Over & Dubbing",
    ],
    gallery: [
      "https://images.unsplash.com/photo-1525547719571-a2d4ac8945e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.pexels.com/photos/3807755/pexels-photo-3807755.jpeg?auto=compress&cs=tinysrgb&w=1200",
    ],
  },
  {
    slug: "3d-animated-explainer",
    category: "Animation & VFX",
    title: "3D Animated Explainer Series",
    summary:
      "A series of stylised 3D explainer films that turned a complex, abstract product into a story audiences actually understood — and shared.",
    heroImage:
      "https://images.unsplash.com/photo-1600869009498-8d429f88d4f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    client: "B2B SaaS Company",
    industry: "Technology / SaaS",
    duration: "12 weeks",
    deliverables: ["5-Part 3D Series", "Motion Graphics", "Sound Design"],
    metrics: [
      { value: "2.4M", label: "Views across channels" },
      { value: "+72%", label: "Explainer watch-through" },
      { value: "5", label: "Films in the series" },
    ],
    challenge:
      "The product was powerful but invisible — an abstract platform that sales teams struggled to explain and prospects struggled to picture.",
    approach: [
      {
        title: "Storyboard the abstract",
        body:
          "We developed a visual metaphor and script that made an intangible platform feel concrete and human.",
      },
      {
        title: "Signature 3D style",
        body:
          "A bespoke look — clean geometry, brand-navy palette and tactile materials — kept all five films unmistakably on-brand.",
      },
      {
        title: "Motion + sound as one",
        body:
          "Custom motion graphics and original sound design gave each film rhythm, clarity and polish.",
      },
    ],
    results: [
      "2.4M views across web, social and sales channels",
      "72% average watch-through on the flagship film",
      "Shortened sales cycles with a reusable explainer toolkit",
      "A distinctive visual language adopted brand-wide",
    ],
    services: ["3D Animation", "Motion Graphics", "Storyboarding", "Sound Editing", "VFX"],
    gallery: [
      "https://images.unsplash.com/photo-1716681864557-c9ca7b2dde63?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.unsplash.com/photo-1547194936-28214bd75193?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.unsplash.com/photo-1600869009498-8d429f88d4f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    ],
  },
  {
    slug: "corporate-brand-film",
    category: "Video Production",
    title: "Corporate Brand Film",
    summary:
      "A cinematic brand film shot across three sites that captured a company's culture and mission — used at the top of every pitch, event and careers page.",
    heroImage:
      "https://images.unsplash.com/photo-1628571201556-82dd32d5398b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    client: "Financial Services Enterprise",
    industry: "Corporate / Finance",
    duration: "8 weeks",
    deliverables: ["Hero Brand Film", "Social Cutdowns", "Event Loop"],
    metrics: [
      { value: "3", label: "Locations filmed" },
      { value: "+120%", label: "Careers-page engagement" },
      { value: "6", label: "Cutdowns delivered" },
    ],
    challenge:
      "The company had a compelling story but no single asset that told it — pitches and events relied on slide decks that fell flat.",
    approach: [
      {
        title: "Find the human thread",
        body:
          "Through interviews we uncovered the real stories behind the brand and built a narrative around the people, not the product.",
      },
      {
        title: "Cinematic multi-site shoot",
        body:
          "A tight, well-planned production captured three locations in a single week with a full crew and lighting package.",
      },
      {
        title: "One film, many formats",
        body:
          "We delivered a hero film plus social cutdowns and a silent event loop, all cut from the same footage.",
      },
    ],
    results: [
      "A flagship film now opening every major pitch and event",
      "120% increase in careers-page engagement",
      "Six ready-to-post cutdowns for social and recruiting",
      "A cohesive story that unified marketing and HR",
    ],
    services: ["Corporate Video", "Cinematography", "Video Compositing", "Sound Editing"],
    gallery: [
      "https://images.unsplash.com/photo-1778166226816-9ce348801b2e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.unsplash.com/photo-1673557478884-5017643d6b5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.unsplash.com/photo-1628571201556-82dd32d5398b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    ],
  },
  {
    slug: "podcast-voiceover-studio",
    category: "Audio & Media",
    title: "Podcast & Voice-Over Studio",
    summary:
      "A turnkey podcast and voice-over production line — from recording and editing to multilingual dubbing — that let a brand publish studio-quality audio every week.",
    heroImage:
      "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    client: "Media & Publishing Brand",
    industry: "Media / Publishing",
    duration: "Ongoing",
    deliverables: ["Weekly Podcast Production", "Voice-Over", "Multilingual Dubbing"],
    metrics: [
      { value: "40+", label: "Episodes produced" },
      { value: "5", label: "Languages dubbed" },
      { value: "1 wk", label: "Idea-to-publish turnaround" },
    ],
    challenge:
      "The brand wanted a consistent weekly show but lacked the studio, workflow and audio expertise to produce broadcast-quality episodes on schedule.",
    approach: [
      {
        title: "A repeatable production line",
        body:
          "We set up a reliable pipeline — recording, editing, mixing and mastering — so every episode hit the same quality bar.",
      },
      {
        title: "Professional voice & sound",
        body:
          "Studio voice-over, clean sound editing and tasteful design gave the show a polished, unmistakable identity.",
      },
      {
        title: "Reach new audiences",
        body:
          "Multilingual dubbing extended each episode into five languages without re-recording the talent.",
      },
    ],
    results: [
      "40+ studio-quality episodes shipped on a weekly cadence",
      "Consistent, professional sound and identity",
      "Five-language dubbing expanding global reach",
      "A one-week idea-to-publish turnaround",
    ],
    services: ["Podcast Production", "Audio Recording", "Voice-Over", "Dubbing", "Sound Editing"],
    gallery: [
      "https://images.unsplash.com/photo-1531651008558-ed1740375b39?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.unsplash.com/photo-1589903308904-1010c2294adc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
      "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    ],
  },
];

export const getCaseStudy = (slug) => CASE_STUDIES.find((c) => c.slug === slug);
export const getNextCaseStudy = (slug) => {
  const i = CASE_STUDIES.findIndex((c) => c.slug === slug);
  return CASE_STUDIES[(i + 1) % CASE_STUDIES.length];
};

export default CASE_STUDIES;
